import { hasDayPassed, isBeforeToday } from "./dateUtils";
import { CustomerData, ProjectDescriptionData, ProjectKeyData } from "../types/projectTypes";

/**
 * Validates the project key data
 * @param projectKeyDataErrors - The errors of the project key data
 * @param projectKeyData - The project key data
 * @param setProjectKeyDataErrors - Function to set the project key data errors
 * @returns - True if there are errors, false otherwise
 * @description This function validates the project key data. It checks if the project name is set, if the estimated project start is in the future, if the estimated project duration is set, if the estimated project euro per BT is set and greater than 0, if the estimated project BT min and max are set and greater than 0, if the estimated project member min and max are set and greater than 0, and if the application end date is set and in the future.
 */
export const validateProjectKeyDataTendering = (
  projectKeyDataErrors: { [key: string]: boolean },
  projectKeyData: ProjectKeyData,
  setProjectKeyDataErrors: React.Dispatch<
    React.SetStateAction<{
      [key: string]: boolean;
    }>
  >
) => {
  let hasErrors = false;
  const newErrors = { ...projectKeyDataErrors };
  if (!projectKeyData.projectName || projectKeyData.projectName.trim() === "") {
    newErrors.projectName = true;
    hasErrors = true;
  } else {
    newErrors.projectName = false;
  }
  if (!projectKeyData.estimatedProjectStart || isBeforeToday(projectKeyData.estimatedProjectStart)) {
    newErrors.estimatedProjectStart = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectStart = false;
  }
  if (!projectKeyData.estimatedProjectDuration) {
    newErrors.estimatedProjectDuration = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectDuration = false;
  }
  if (!projectKeyData.estimatedProjectEuroPerBT || projectKeyData.estimatedProjectEuroPerBT < 0) {
    newErrors.estimatedProjectEuroPerBT = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectEuroPerBT = false;
  }
  if (
    !projectKeyData.estimatedProjectBTmin ||
    projectKeyData.estimatedProjectBTmin < 0 ||
    !Number.isInteger(projectKeyData.estimatedProjectBTmin)
  ) {
    newErrors.estimatedProjectBTmin = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectBTmin = false;
  }
  if (
    !projectKeyData.estimatedProjectBTmax ||
    projectKeyData.estimatedProjectBTmax < 0 ||
    !Number.isInteger(projectKeyData.estimatedProjectBTmax) ||
    (projectKeyData.estimatedProjectBTmin &&
      projectKeyData.estimatedProjectBTmax < projectKeyData.estimatedProjectBTmin)
  ) {
    newErrors.estimatedProjectBTmax = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectBTmax = false;
  }
  if (
    !projectKeyData.estimatedProjectMemberMin ||
    projectKeyData.estimatedProjectMemberMin < 0 ||
    !Number.isInteger(projectKeyData.estimatedProjectMemberMin)
  ) {
    newErrors.estimatedProjectMemberMin = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectMemberMin = false;
  }
  if (
    !projectKeyData.estimatedProjectMemberMax ||
    projectKeyData.estimatedProjectMemberMax < 0 ||
    !Number.isInteger(projectKeyData.estimatedProjectMemberMax) ||
    (projectKeyData.estimatedProjectMemberMin &&
      projectKeyData.estimatedProjectMemberMax < projectKeyData.estimatedProjectMemberMin)
  ) {
    newErrors.estimatedProjectMemberMax = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectMemberMax = false;
  }
  if (!projectKeyData.applicationEnd1 || hasDayPassed(projectKeyData.applicationEnd1)) {
    newErrors.applicationEnd1 = true;
    hasErrors = true;
  } else {
    newErrors.applicationEnd1 = false;
  }
  setProjectKeyDataErrors(newErrors);
  return hasErrors;
};

/**
 * Validates the customer data
 * @param customerDataErrors - The errors of the customer data
 * @param customerData - The customer data
 * @param setCustomerDataErrors - Function to set the customer data errors
 * @returns - True if there are errors, false otherwise
 * @description This function validates the customer data. It checks if the customer is set, if the industry is set, if the new customer is set, if the acquisition method is set, if the contact person is set and if the acquisition method is set.
 */
export const validateCustomerData = (
  customerDataErrors: { [key: string]: boolean },
  customerData: CustomerData,
  setCustomerDataErrors: React.Dispatch<
    React.SetStateAction<{
      [key: string]: boolean;
    }>
  >
) => {
  let hasErrors = false;
  const newErrors = { ...customerDataErrors };
  if (customerData.companyId === -1) {
    newErrors.customer = true;
    hasErrors = true;
  } else {
    newErrors.customer = false;
  }
  if (!customerData.industry) {
    newErrors.industry = true;
    hasErrors = true;
  } else {
    newErrors.industry = false;
  }
  if (customerData.newCustomer === undefined) {
    newErrors.newCustomer = true;
    hasErrors = true;
  } else {
    newErrors.newCustomer = false;
  }
  if (!customerData.acquisitor) {
    newErrors.acquisitor = true;
    hasErrors = true;
  } else {
    newErrors.acquisitor = false;
  }
  if (!customerData.acquisitionMethod) {
    newErrors.acquisitionMethod = true;
    hasErrors = true;
  } else {
    newErrors.acquisitionMethod = false;
  }
  if (!customerData.contactPerson) {
    newErrors.contactPerson = true;
    hasErrors = true;
  } else {
    newErrors.contactPerson = false;
  }
  setCustomerDataErrors(newErrors);
  return hasErrors;
};

/**
 * Validates the project description data
 * @param projectDescriptionDataErrors - The errors of the project description data
 * @param projectDescriptionData - The project description data
 * @param setProjectDescriptionDataErrors - Function to set the project description data errors
 * @returns - True if there are errors, false otherwise
 * @description This function validates the project description data. It checks if the core competencies are set.
 */
export const validateProjectDescriptionData = (
  projectDescriptionDataErrors: { [key: string]: boolean },
  projectDescriptionData: ProjectDescriptionData,
  setProjectDescriptionDataErrors: React.Dispatch<
    React.SetStateAction<{
      [key: string]: boolean;
    }>
  >
) => {
  let hasErrors = false;
  const newErrors = { ...projectDescriptionDataErrors };
  if (!projectDescriptionData.coreCompetencies || projectDescriptionData.coreCompetencies.length === 0) {
    newErrors.coreCompetencies = true;
    hasErrors = true;
  } else {
    newErrors.coreCompetencies = false;
  }
  setProjectDescriptionDataErrors(newErrors);
  return hasErrors;
};
